/**
 * Modal pour exporter un template de prise de besoin en Excel
 * Permet de sélectionner le métier et génère un fichier Excel pour prise de besoin manuelle
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, FileSpreadsheet, Briefcase } from 'lucide-react';
import { METIERS } from '../domain/metier/metierTypes';
import { INTAKE_TEMPLATES, getIntakeTemplate } from '../domain/besoins/templates';
import { exportTemplateToExcel } from '../services/excelExportService';

interface ExportTemplateExcelModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId?: string; // Template pré-sélectionné (optionnel)
}

export default function ExportTemplateExcelModal({
  isOpen,
  onClose,
  templateId,
}: ExportTemplateExcelModalProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(templateId || '');
  const [selectedMetierId, setSelectedMetierId] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const selectedTemplate = selectedTemplateId ? getIntakeTemplate(selectedTemplateId) : null;
  const selectedMetier = METIERS.find((m) => m.id === selectedMetierId);

  const handleExport = async () => {
    if (!selectedTemplateId || !selectedMetierId) {
      return;
    }

    setIsExporting(true);
    try {
      await exportTemplateToExcel(selectedTemplateId, selectedMetierId);
      // Fermer la modal après export réussi
      setTimeout(() => {
        onClose();
        setIsExporting(false);
        // Reset
        setSelectedTemplateId(templateId || '');
        setSelectedMetierId('');
      }, 500);
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      alert('Erreur lors de l\'export. Veuillez réessayer.');
      setIsExporting(false);
    }
  };

  const canExport = selectedTemplateId && selectedMetierId && !isExporting;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden pointer-events-auto flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-600 via-blue-600 to-indigo-600 text-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                      <FileSpreadsheet className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Exporter template en Excel</h2>
                      <p className="text-sm text-white/90 font-medium">
                        Générer un fichier Excel pour prise de besoin manuelle
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Sélection du template */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Template de prise de besoin <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-sm text-gray-800 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                  >
                    <option value="">-- Sélectionner un template --</option>
                    {INTAKE_TEMPLATES.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} (v{template.version})
                      </option>
                    ))}
                  </select>
                  {selectedTemplate && (
                    <p className="text-xs text-gray-600 mt-1">{selectedTemplate.description}</p>
                  )}
                </div>

                {/* Sélection du métier */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Métier du client <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                      value={selectedMetierId}
                      onChange={(e) => setSelectedMetierId(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-white text-sm text-gray-800 focus:ring-2 focus:ring-bz-blue focus:border-bz-blue"
                    >
                      <option value="">-- Sélectionner un métier --</option>
                      {METIERS.map((metier) => (
                        <option key={metier.id} value={metier.id}>
                          {metier.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedMetier && (
                    <p className="text-xs text-gray-600 mt-1">
                      Le fichier Excel inclura les questions spécifiques à ce métier
                    </p>
                  )}
                </div>

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs text-blue-900">
                    <strong>Note :</strong> Le fichier Excel généré contiendra toutes les questions du template
                    sélectionné, organisées par phase, ainsi que les questions spécifiques au métier choisi. Vous
                    pourrez l&apos;utiliser pour prendre les besoins manuellement avec vos clients.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleExport}
                  disabled={!canExport}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    canExport
                      ? 'bg-gradient-to-r from-emerald-600 to-blue-600 text-white hover:from-emerald-700 hover:to-blue-700 shadow-lg'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Export en cours...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Exporter en Excel</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}










